import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle } from 'react-icons/fa';
import { useDebouncedCallback } from 'use-debounce';
import SimpleToggleSwitch from 'common/components/simpleToggleSwitch/SimpleToggleSwitch';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import InputSearch from 'common/components/inputSearch/InputSearch';
import PrivacyService from 'api/privacy/PrivacyService';
import { PrivacyDriverDto } from 'api/privacy/models/PrivacyDriverDto';
import { PrivacySearchCriteria } from 'api/privacy/models/PrivacySearchCriteria';
import { Paged } from 'api/common/types/Page';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import styles from '../PrivacyTab.module.scss';

type Props = {
    onToggle?: () => void;
};

const DriversPrivacySection: React.FC<Props> = ({ onToggle }: Props) => {
    const { t } = useTranslation();
    const [driversPage, setDriversPage] = useState<Paged<PrivacyDriverDto>>();
    const [criteria, setCriteria] = useState<PrivacySearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'u.real_name',
        orderBy: 'asc'
    });
    const isFirstLoad = useRef(true);

    const getData = useCallback(async () => {
        try {
            const page = await PrivacyService.getDrivers(criteria);
            setDriversPage(page);
        } catch {
            // Error handled by Http interceptor
        }
    }, [criteria]);

    useEffect(() => {
        isFirstLoad.current = false;
        getData();
    }, [getData]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: isFilterAsc ? 'asc' : 'desc', orderColumn: field });
    };

    const debounced = useDebouncedCallback((value: string) => {
        setCriteria({ ...criteria, page: 1, allIn: value || undefined });
    }, 500);

    const [togglingId, setTogglingId] = useState<string | null>(null);

    const handleTogglePrivacy = async (driver: PrivacyDriverDto) => {
        if (togglingId) return;
        setTogglingId(driver.driverId);
        try {
            await PrivacyService.togglePrivacy({
                entityId: driver.driverId,
                entityType: 'driver',
                enabled: !driver.privacyEnabled,
            });
            await getData();
            onToggle?.();
        } catch {
            // Error handled by Http interceptor
        } finally {
            setTogglingId(null);
        }
    };

    const activeCount = useMemo(() => driversPage?.items.filter(d => d.privacyEnabled || d.hasLiveSchedule).length ?? 0, [driversPage]);

    const tableColumns: ListingTableColumn<PrivacyDriverDto>[] = [
        {
            name: t('privacy.history_col_driver') as string,
            searchField: 'u.real_name',
            onSearch: onTableFilter,
            renderCell: row => <>{row.driverName}</>
        },
        {
            name: t('vehicle.registration') as string,
            renderCell: row => {
                if (!row.vehicleRegistrations) return <>-</>;
                const regs = row.vehicleRegistrations.split(', ');
                const maxVisible = 3;
                const visible = regs.slice(0, maxVisible);
                const remaining = regs.slice(maxVisible);
                return (
                    <div className={styles.contactsCell}>
                        <span>{visible.join(', ')}</span>
                        {remaining.length > 0 && (
                            <div className={styles.contactsOverflow}>
                                <span className={styles.contactsMoreBadge}>+{remaining.length}</span>
                                <div className={styles.contactsTooltip}>
                                    {remaining.map(r => <div key={r}>{r}</div>)}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            name: t('privacy.manual') as string,
            width: '100px',
            cellAlignment: 'center',
            preventClick: true,
            renderCell: row => (
                <SimpleToggleSwitch
                    checked={row.privacyEnabled}
                    onChange={() => handleTogglePrivacy(row)}
                />
            )
        }
    ];

    return (
        <>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>
                            {t('privacy.drivers_privacy_title') as string}
                            <FaInfoCircle
                                className={styles.titleInfoIcon}
                                title={t('privacy.manual_tooltip') as string}
                            />
                        </div>
                        <div className={styles.cardDesc}>{t('privacy.drivers_privacy_desc') as string}</div>
                    </div>
                    <div className={styles.cardMeta}>
                        <div className={styles.cardStats}>
                            <div className={styles.statItem}>
                                <div className={styles.statLabel}>{t('privacy.active') as string}</div>
                                <div className={styles.statVal}>{activeCount}</div>
                            </div>
                            <div className={styles.statDivider} />
                            <div className={styles.statItem}>
                                <div className={styles.statLabel}>{t('privacy.total') as string}</div>
                                <div className={styles.statVal}>{driversPage?.totalItems ?? 0}</div>
                            </div>
                        </div>
                        <InputSearch
                            onChangeValue={debounced}
                            placeholder={t('privacy.search_driver') as string}
                        />
                    </div>
                </div>
                <div className={styles.cardBodyNoPadding}>
                    <ListingTable
                        columns={tableColumns}
                        rows={driversPage?.items || []}
                        allowHover={true}
                        initialSearch={{ colField: criteria.orderColumn ?? 'u.real_name', isOrderAsc: criteria.orderBy === 'asc' }}
                    />
                    {driversPage && <PaginationWithInfo
                        itemName={t('privacy.section_drivers') as string}
                        currentPage={driversPage.currentPage || 1}
                        pageItems={driversPage.items.length || 0}
                        totalItems={driversPage.totalItems || 0}
                        onChange={page => setCriteria({ ...criteria, page })}
                    />}
                </div>
            </div>

        </>
    );
};

export default DriversPrivacySection;
